import { Invoice } from './invoice.model';

export interface ISplunkMetric {
  metricKey:
    | MetricKey
    | ResultMetricKey
    | PaymentPlanMetricKey
    | EstimateVerificationMetricKey;
  metricValue?: string | boolean | number | Invoice[];
}

export enum MetricKey {
  dashboardId = 'DashboardID',
  eventCode = 'EventCode',
  lpId = 'LPID',
  invoiceNumber = 'InvoiceNumber',
  specimenNumber = 'SpecimenNumber',
  templateId = 'TemplateId',
  mobileNumber = 'MobileNumber',
  userType = 'UserType',
  alertMsg = 'AlertMessage',
  reasonCode = 'ReasonCode',
  sessionID = 'SessionID',
  showErrorFlag = 'ShowErrorFlag',
  patientId = 'PatientId',
  balanceDue = 'BalanceDue',
  dunningCycle = 'DunningCycle',
  transactionId = 'TransactionId',
  pageUrl = 'PageUrl',
}

export enum EventCode {
  landed = 'LANDED',
  paymentDetail = 'PAYMENT_DETAIL',
  paymentReview = 'PAYMENT_REVIEW',
  paymentConfirmation = 'PAYMENT_CONFIRMATION',
  textOptInLanded = 'TEXT_OPT_IN_LANDED',
  updateInfo = 'UPDATE_INFO',
  registrationLanded = 'REGISTRATION_LANDED',
  registrationSubmit = 'REGISTRATION_SUBMIT',
  registrationComplete = 'REGISTRATION_COMPLETE',
  loginLanded = 'LOGIN_LANDED',
  mfaReview = 'MFA_REVIEW',
  mfaSmsEnrollment = 'MFA_SMS_ENROLLMENT',
  mfaRemoveFactor = 'MFA_REMOVE_FACTOR',
  mfaOktaEnrollment = 'MFA_OKTA_ENROLLMENT',
  mfaGoogleEnrollment = 'MFA_GOOGLE_ENROLLMENT',
  mfaPasswordValidation = 'MFA_PASSWORD_VALIDATION',
  mfaDecline = 'MFA_DECLINE',
  mfaLater = 'MFA_DO_IT_LATER',
  mfaTurnOn = 'MFA_TURN_ON',
  mfaUserPreference = 'MFA_LOADING_USER_FACTOR',
  mfaDashBoard = 'MFA_PORTAL_DASHBOARD_COMPONENT',
  mfaOpenModal = 'MFA_OPEN_MODAL',
  mfaDoneModal = 'MFA_DONE_MODAL',
  mfaOktaCancel = 'MFA_OKTA_CANCEL',
  mfaSetup = 'MFA_SETUP',
  mfaGoogleCancel = 'MFA_GOOGLE_CANCEL',
  mfaSmsFactor = 'MFA_SMS_FACTOR',
  mfaOktaFactor = 'MFA_OKTA_FACTOR',
  mfaGoogleFactor = 'MFA_GOOGLE_FACTOR',
  updateInsurance = 'RU_UPDATE_INSURANCE',
  paymentPlanReview = 'PAYMENT_PLAN_REVIEW',
  paymentPlanCreateStart = 'PAYMENT_PLAN_CREATE_START',
  paymentMethod = 'PAYMENT_PLAN_METHOD',
  paymentPlanTerms = 'PAYMENT_PLAN_TERMS',
  paymentPlanLogin = 'PAYMENT_PLAN_LOGIN',
  PaymentPlanConfirmation = 'PAYMENT_PLAN_CONFIRMATION',
  SMSInvoiceValidation = 'SMSInvoiceValidation',
  // Payment authorization letter (NACHA / card-capture) verification page.
  //
  // Emitted by the frontend. These describe client-side delivery only — whether
  // the browser decoded and rendered the PDF — because the frontend cannot see
  // the authorization type (it lives inside the encrypted transaction ID).
  receiptVerificationPageLoaded = 'RECEIPT_VERIFICATION_PAGE_LOADED',
  receiptPdfRenderAttempted = 'RECEIPT_PDF_RENDER_ATTEMPTED',
  receiptPdfRendered = 'RECEIPT_PDF_RENDERED',
  receiptPdfRenderFailed = 'RECEIPT_PDF_RENDER_FAILED',
  receiptVerificationPopupBlocked = 'RECEIPT_VERIFICATION_POPUP_BLOCKED',
  //
  // Emitted by patient-portal-ws, which decrypts the transaction ID and so is
  // the only side that knows NACHA from capture. Kept here to keep the TS enum
  // mirrored with the backend's; not referenced by frontend code.
  nachaVerificationPageLoaded = 'NACHA_VERIFICATION_PAGE_LOADED',
  captureVerificationPageLoaded = 'CAPTURE_VERIFICATION_PAGE_LOADED',
  nachaDownloadAttempted = 'NACHA_DOWNLOAD_ATTEMPTED',
  captureDownloadAttempted = 'CAPTURE_DOWNLOAD_ATTEMPTED',
  nachaDownloadSuccess = 'NACHA_DOWNLOAD_SUCCESS',
  captureDownloadSuccess = 'CAPTURE_DOWNLOAD_SUCCESS',
  nachaDownloadFailed = 'NACHA_DOWNLOAD_FAILED',
  captureDownloadFailed = 'CAPTURE_DOWNLOAD_FAILED',
  receiptDownloadAttempted = 'RECEIPT_DOWNLOAD_ATTEMPTED',
  receiptDownloadSuccess = 'RECEIPT_DOWNLOAD_SUCCESS',
  receiptDownloadFailed = 'RECEIPT_DOWNLOAD_FAILED',
  receiptVerificationLinkExpired = 'RECEIPT_VERIFICATION_LINK_EXPIRED',
  // Patient estimate verification events
  estimateVerificationPageLoaded = 'ESTIMATE_VERIFICATION_PAGE_LOADED',
  estimateVerificationAttempted = 'ESTIMATE_VERIFICATION_ATTEMPTED',
  estimateVerificationSuccess = 'ESTIMATE_VERIFICATION_SUCCESS',
  estimateVerificationFailed = 'ESTIMATE_VERIFICATION_FAILED',
  estimatePdfViewed = 'ESTIMATE_PDF_VIEWED',
}

export interface IBadDebtMetric {
  eventCode: string;
  templateId: string;
  lpId?: string;
  invoiceNumber?: string;
  specimenNumber?: string;
  mobileNumber?: string;
  userType: string;
  dashboardId?: string;
  alertMsg?: string;
  reasonCode?: string;
  sessionID?: string;
  showErrorFlag?: boolean;
  patientId?: string;
  balanceDue?: string;
  dunningCycle?: string;
}

export interface IMfaMetric {
  eventCode: string;
  mobileNumber?: string;
  templateId: string;
  userType: string;
  dashboardId?: string;
  alertMsg?: string;
  reasonCode?: string;
  sessionID?: string;
}

export interface IPaymentPlanMetric {
  dashboardId?: string;
  eventCode: string;
  invoiceNumber: string;
  templateId: string;
  userType: string;
  allInvoices?: Invoice[];
  reasonCode?: string;
  alertMsg?: string;
  sessionID?: string;
  showErrorFlag?: boolean;
}

export enum PaymentPlanMetricKey {
  dashboardId = 'DashboardID',
  eventCode = 'EventCode',
  invoiceNumber = 'InvoiceNumber',
  allInvoices = 'AllInvoices',
  templateId = 'TemplateId',
  userType = 'UserType',
  reasonCode = 'ReasonCode',
  alertMsg = 'AlertMsg',
  sessionID = 'SessionID',
  showErrorFlag = 'ShowErrorFlag',
}
export interface IResultMetric {
  dashboardId: string;
  eventCode: string;
  userType: string;
  userId?: string;
  patientMasterId?: number;
  statusCode?: number;
  alertMsg?: string;
  resultId?: string;
}

export enum ResultMetricKey {
  dashboardId = 'DashboardID',
  eventCode = 'EventCode',
  userType = 'UserType',
  userId = 'UserID',
  patientMasterId = 'PatientMasterID',
  statusCode = 'StatusCode',
  alertMsg = 'AlertMsg',
  resultId = 'ResultID',
}

export interface IEstimateVerificationMetric {
  eventCode: string;
  shortKey: string;
  userType: string;
  lpId?: string;
  accessionNumber?: string;
  statusCode?: number;
  alertMsg?: string;
  sessionID?: string;
}

export enum EstimateVerificationMetricKey {
  eventCode = 'EventCode',
  shortKey = 'ShortKey',
  userType = 'UserType',
  lpId = 'LPID',
  accessionNumber = 'AccessionNumber',
  statusCode = 'StatusCode',
  alertMsg = 'AlertMsg',
  sessionID = 'SessionID',
}
